<template>
	<v-card
		class="d-flex flex-column pb-5"
		:class="{ 'paper-table--embedded': embedded }"
	>
		<v-toolbar color="primary" class="pr-3" dark flat>
			<v-toolbar-title style="flex: 0 1 auto">
				{{ showDownload && downloadMode ? "Select papers to download" : title }}
			</v-toolbar-title>

			<!-- Search lives in the toolbar (white solo pill) so every consumer of
			     this shared table gets it for free. Hidden when `embedded`: the
			     parent (Project View) supplies its own search bar and drives this
			     table through the `externalSearch` prop instead. -->
			<v-text-field
				v-if="!embedded"
				v-model="searchKey"
				prepend-inner-icon="mdi-magnify"
				placeholder="Search papers..."
				density="compact"
				variant="solo"
				flat
				rounded
				hide-details
				clearable
				bg-color="white"
				class="flex-grow-0 ml-4 paper-toolbar-search"
			/>

			<v-spacer />

			<v-btn
				v-if="!queueMode"
				color="grey-lighten-5"
				class="mr-3 bg-white text-primary"
				elevation="2"
				@click="displayDeleted = !displayDeleted"
			>
				<template v-if="!displayDeleted">
					<v-icon start> mdi-eye </v-icon>

					Show deleted
				</template>

				<template v-else>
					<v-icon start> mdi-eye-off </v-icon>

					Hide deleted
				</template>
			</v-btn>

			<v-btn
				v-if="!queueMode && showDownload && downloadMode"
				color="secondary"
				class="mr-3"
				elevation="2"
				:disabled="downloads.length < 1"
				@click="downloadPaperData"
			>
				<v-icon start> mdi-download </v-icon>

				Download selected
			</v-btn>

			<v-btn
				v-if="!queueMode && showDownload"
				:class="
					downloadMode
						? 'mr-3 bg-primary text-white'
						: 'mr-3 bg-white text-primary'
				"
				elevation="2"
				@click="downloadMode = !downloadMode"
			>
				<v-icon v-if="!downloadMode" start> mdi-download </v-icon>

				{{ downloadMode ? "Cancel download" : "Download data" }}
			</v-btn>

			<!-- Shared Add/Edit dialog. Rendered for every editable (non-queue)
			     mode so the row-level Edit pencil (which opens it via
			     `dialog = true`) works in orphaned + project-embedded tables too;
			     only the "Add new paper" activator is gated on `showAddPaper`. -->
			<v-dialog
				v-if="!queueMode"
				v-model="dialog"
				max-width="800px"
				transition="fade-transition"
				@click:outside="clearSelectedPaper"
			>
				<template #activator="{ props }">
					<v-btn
						v-if="showAddPaper"
						v-bind="props"
						class="bg-secondary"
						elevation="2"
						:disabled="downloadMode"
					>
						<v-icon start> mdi-plus </v-icon>

						Add new paper
					</v-btn>
				</template>

				<paper-edit
					ref="paperEdit"
					:default-paper="selectedPaper"
					:locked-project="lockedProject"
					@cancel-paper-edit="closeDialog"
					@refresh="closeDialogAndRefresh"
				/>
			</v-dialog>

			<v-dialog
				v-if="!queueMode"
				v-model="deleteDialog"
				max-width="800px"
				transition="fade-transition"
				@click:outside="clearSelectedPaper"
			>
				<v-card>
					<v-toolbar color="primary" dark flat>
						<v-toolbar-title>Delete paper</v-toolbar-title>
						<v-hover-icon class="mr-3" />
					</v-toolbar>

					<v-card-title class="text-center py-4">
						Are you sure you want to delete this paper?
					</v-card-title>

					<v-divider />

					<v-card-subtitle class="text-center py-4">
						{{ selectedPaper.name }}
					</v-card-subtitle>

					<v-divider />

					<v-card-actions class="py-5">
						<v-spacer />

						<v-btn class="bg-secondary" elevation="2" @click="deletePaper">
							Yes
						</v-btn>

						<v-btn class="bg-grey" elevation="2" @click="closeDeleteDialog">
							No
						</v-btn>

						<v-spacer />
					</v-card-actions>
				</v-card>
			</v-dialog>
			<v-dialog
				v-model="restoreDialog"
				max-width="800px"
				transition="fade-transition"
			>
				<v-card>
					<v-toolbar color="primary" dark flat>
						<v-toolbar-title>Restore paper</v-toolbar-title>
						<v-hover-icon class="mr-3" />
					</v-toolbar>

					<v-card-title class="text-center py-4">
						Are you sure you want to restore this paper?
					</v-card-title>

					<v-divider />

					<v-card-subtitle class="text-center py-4">
						{{ selectedPaper.name }}
					</v-card-subtitle>

					<v-divider />

					<v-card-actions class="py-5">
						<v-spacer />
						<v-btn class="bg-secondary" elevation="2" @click="restorePaper">
							Yes
						</v-btn>
						<v-btn class="bg-grey" elevation="2" @click="closeRestoreDialog">
							No
						</v-btn>
						<v-spacer />
					</v-card-actions>
				</v-card>
			</v-dialog>
		</v-toolbar>
		<v-data-table-server
			v-model="downloads"
			v-model:options="options"
			v-model:items-per-page="options.itemsPerPage"
			v-model:page="options.page"
			class="paper-table"
			item-key="uuid"
			:row-props="getRowProps"
			:headers="headers"
			:items="papers"
			:items-length="totalPapers"
			:loading="loading"
			:no-data-text="noDataText"
			:disable-sort="queueMode"
			:show-select="showSelect"
			return-object
			:disable-pagination="trainingQueueMode"
			:hide-default-footer="trainingQueueMode"
		>
			<template
				v-if="queueMode && isAdmin"
				#item.queue_admin_actions="{ item }"
			>
				<v-tooltip location="top" open-delay="500">
					<template #activator="{ props }">
						<v-text-field
							v-bind="props"
							v-model="item.priority"
							class="mt-0 pt-0 paper-table-priority-text"
							type="number"
							density="compact"
							hide-details
							@change="setPriority(item)"
						/>
					</template>
					<span>Assign priority</span>
				</v-tooltip>
			</template>

			<template #item.name="{ item }">
				<div
					v-if="!queueMode && item.preextract_pending"
					class="d-flex align-center"
				>
					<v-tooltip location="bottom" open-delay="500">
						<template #activator="{ props }">
							<v-icon
								v-bind="props"
								size="18"
								color="warning"
								class="flex-shrink-0 mr-2"
								:aria-label="preparingMarkerTooltip"
								aria-hidden="false"
								role="img"
							>
								mdi-alert-circle
							</v-icon>
						</template>

						<span>{{ preparingMarkerTooltip }}</span>
					</v-tooltip>

					<div>
						<span v-if="item.name">
							{{ item.name }}
						</span>

						<span v-else class="font-italic text--disabled"> Unnamed </span>
					</div>
				</div>

				<template v-else>
					<span v-if="item.name">
						{{ item.name }}
					</span>

					<span v-else class="font-italic text--disabled"> Unnamed </span>
				</template>
			</template>

			<template #item.projects="{ item }">
				<div class="d-flex justify-center flex-wrap my-2">
					<v-chip
						v-for="project of item.projects"
						:key="project.uuid"
						size="x-small"
						variant="flat"
						class="bg-surface-tint text-fg-1"
					>
						{{ project.name }}
					</v-chip>

					<v-tooltip
						v-if="!item.projects.length"
						location="bottom"
						open-delay="500"
					>
						<template #activator="{ props }">
							<v-icon v-bind="props" color="error" class="cursor-default">
								mdi-alert-circle
							</v-icon>
						</template>

						<span>No associated projects</span>
					</v-tooltip>
				</div>
			</template>

			<template #item.file_count="{ item }">
				<v-menu
					v-if="!queueMode && item.files.length > 0"
					close-on-content-click
				>
					<template #activator="{ props }">
						<a
							v-bind="props"
							class="text-decoration-underline text-primary cursor-pointer"
						>
							{{ getFileCount(item) }}
						</a>
					</template>

					<v-list density="compact">
						<v-list-item v-for="file in item.files" :key="file.id">
							<a
								class="text-decoration-underline cursor-pointer text-primary"
								@click="downloadFile(file)"
							>
								{{ file.name }}
							</a>
						</v-list-item>
					</v-list>
				</v-menu>

				<div v-else>
					{{ getFileCount(item) }}
				</div>
			</template>

			<template #item.created_at="{ item }">
				{{ getCreatedTimestamp(item) }}
			</template>

			<template #item.in_progress="{ item }">
				<div class="d-flex justify-center flex-wrap my-2">
					<v-chip
						v-for="dataEntry of inProgressDataEntries(item)"
						:key="dataEntry.uuid"
						size="x-small"
						variant="flat"
						class="bg-surface-tint text-fg-1"
					>
						{{ dataEntry.user.email }}
					</v-chip>
					<v-tooltip
						v-if="!inProgressDataEntries(item).length"
						location="bottom"
						open-delay="500"
					>
						<template #activator="{ props }">
							<v-icon v-bind="props" class="cursor-default" size="large">
								mdi-account-off-outline
							</v-icon>
						</template>

						<span>No extractions in-progress</span>
					</v-tooltip>
				</div>
			</template>

			<template #item.completed="{ item }">
				<div class="d-flex justify-center flex-wrap my-2">
					<v-chip
						v-for="dataEntry of completedDataEntries(item)"
						:key="dataEntry.uuid"
						size="x-small"
						variant="flat"
						class="bg-surface-tint text-fg-1"
					>
						{{ dataEntry.user.email }}
					</v-chip>
					<v-tooltip
						v-if="!completedDataEntries(item).length"
						location="bottom"
						open-delay="500"
					>
						<template #activator="{ props }">
							<v-icon v-bind="props" class="cursor-default" size="large">
								mdi-account-off-outline
							</v-icon>
						</template>

						<span>No extractions completed</span>
					</v-tooltip>
				</div>
			</template>

			<template v-if="!queueMode" #item.admin_actions="{ item }">
				<v-tooltip v-if="!item.deleted_at" location="bottom" open-delay="500">
					<template #activator="{ props }">
						<v-hover v-slot="{ hover }">
							<v-icon
								v-bind="props"
								class="mr-2"
								:color="hover ? 'secondary' : ''"
								:disabled="downloadMode"
								:class="{ 'cursor-not-allowed': downloadMode }"
								@click="edit(item)"
							>
								mdi-pencil
							</v-icon>
						</v-hover>
					</template>

					<span>Edit</span>
				</v-tooltip>
				<v-tooltip v-if="!item.deleted_at" location="bottom" open-delay="500">
					<template #activator="{ props }">
						<v-hover v-slot="{ hover }">
							<v-icon
								v-bind="props"
								:color="hover ? 'error' : ''"
								:disabled="downloadMode"
								:class="{ 'cursor-not-allowed': downloadMode }"
								@click="remove(item)"
							>
								mdi-delete
							</v-icon>
						</v-hover>
					</template>

					<span>Delete</span>
				</v-tooltip>

				<v-tooltip v-if="item.deleted_at" location="bottom" open-delay="500">
					<template #activator="{ props }">
						<v-hover v-slot="{ hover }">
							<v-icon
								v-bind="props"
								:color="hover ? 'secondary' : ''"
								:disabled="downloadMode"
								:class="{ 'cursor-not-allowed': downloadMode }"
								@click="restore(item)"
							>
								mdi-restore
							</v-icon>
						</v-hover>
					</template>

					<span>Restore</span>
				</v-tooltip>
			</template>

			<template v-else #item.queue_actions="{ item }">
				<v-tooltip location="bottom" open-delay="500">
					<template #activator="{ props }">
						<v-hover v-if="getFileCount(item)" v-slot="{ hover }">
							<v-icon
								v-bind="props"
								:color="hover ? 'secondary' : ''"
								:disabled="downloadMode"
								:class="{ 'cursor-not-allowed': downloadMode }"
								size="large"
								@click="startReadOnlyDataEntry(item)"
							>
								mdi-file-eye-outline
							</v-icon>
						</v-hover>
						<v-icon
							v-else
							v-bind="props"
							class="v-icon--disabled cursor-not-allowed"
							:disabled="downloadMode"
							size="large"
						>
							mdi-file-eye-outline
						</v-icon>
					</template>
					<span>View Paper (Read-only)</span>
				</v-tooltip>
				<v-tooltip location="bottom" open-delay="500">
					<template #activator="{ props }">
						<v-hover v-if="getFileCount(item)" v-slot="{ hover }">
							<v-icon
								v-bind="props"
								:color="hover ? 'secondary' : ''"
								:disabled="downloadMode"
								:class="{
									'cursor-not-allowed': downloadMode,
									'ml-1': true,
								}"
								size="large"
								@click="selectPaper(item)"
							>
								mdi-file-send
							</v-icon>
						</v-hover>
						<v-icon
							v-else
							v-bind="props"
							class="v-icon--disabled cursor-not-allowed ml-1"
							:disabled="downloadMode"
							size="large"
						>
							mdi-file-send
						</v-icon>
					</template>

					<span v-if="getFileCount(item)">{{
						qcMode ? "Start Quality Control" : "Start Extraction"
					}}</span>
					<span v-else>Paper does not have associated files</span>
				</v-tooltip>
			</template>
		</v-data-table-server>

		<v-dialog v-model="unavailableDialogOpen" max-width="450">
			<v-card>
				<v-toolbar color="primary" dark flat>
					<v-toolbar-title>
						{{
							unavailableIsPreparing
								? "Paper not ready yet"
								: "Paper no longer available"
						}}
					</v-toolbar-title>
				</v-toolbar>
				<v-card-text class="pt-4">
					<template v-if="unavailableIsPreparing">
						<p class="text-body-1">
							<strong>{{ unavailablePaperName }}</strong>
						</p>
						<p class="text-body-1 mt-2">{{ preparingMessage }}</p>
					</template>
					<p v-else class="text-body-1">
						<strong>{{ unavailablePaperName }}</strong> has just been claimed by
						another extractor and is no longer in your queue. The queue has been
						refreshed.
					</p>
				</v-card-text>
				<v-card-actions class="px-4 pb-4">
					<v-spacer />
					<v-btn class="bg-secondary" @click="unavailableDialogOpen = false">
						OK
					</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>

		<extraction-select-dialog
			v-model="entrySelectDialogOpen"
			:entries="entrySelectEntries"
			:paper-name="entrySelectPaperName"
			:read-only="entrySelectReadOnly"
			:current-user-uuid="user.uuid"
			@select="onEntrySelected"
		/>
	</v-card>
</template>

<script>
import _ from "lodash";
import fileDownload from "js-file-download";
import { v4 as uuidv4 } from "uuid";
import { useLDFlag } from "launchdarkly-vue-client-sdk";
import { mapActions } from "pinia";
import { useMainStore } from "@/store";
import {
	extractionRoleSlugs,
	PAPER_PREPARING,
	PAPER_PREPARING_MARKER_TOOLTIP,
	PAPER_PREPARING_MESSAGE,
} from "@/constants";
import { filenameFromContentDisposition } from "@/utils/contentDisposition";

import ExtractionSelectDialog from "@/pages/PaperManager/ExtractionSelectDialog";
import PaperEdit from "@/pages/PaperManager/PaperEdit";
import VHoverIcon from "@/components/VHoverIcon";

export default {
	name: "PaperTable",

	components: {
		ExtractionSelectDialog,
		PaperEdit,
		VHoverIcon,
	},

	props: {
		training: Boolean,
		qcMode: Boolean,
		adminQueueMode: Boolean,
		sdeQueueMode: Boolean,
		trainingQueueMode: Boolean,
		// Project View "Papers" tab: scope the admin paper list to one project
		// and pin adds to it (other projects can still be attached). Empty/null
		// keeps the standard all-papers behavior.
		projectUuid: {
			type: String,
			default: "",
		},
		lockedProject: {
			type: Object,
			default: null,
		},
		// Orphaned Papers view: reuse this table for papers with no project. The
		// fetch is filtered to orphans and the always-empty Projects column is
		// dropped; toolbar + row actions stay identical to Paper Management.
		orphanedMode: Boolean,
		// Embedded in a parent that already provides its own chrome (Project
		// View's Papers tab): hide this table's own search field (the parent's
		// search drives it via `externalSearch`) and square off the top corners
		// so the toolbar sits flush under the parent's tab strip.
		embedded: Boolean,
		// Search term supplied by the parent when `embedded`; ignored otherwise
		// (the internal search field owns the term in standalone use).
		externalSearch: {
			type: String,
			default: "",
		},
	},

	// DCTL-2093: when embedded in a parent (ProjectView) the parent handles editing
	// an existing paper, because this table's own edit dialog is suppressed there.
	emits: ["edit-paper"],

	setup() {
		const useSdeV2 = useLDFlag("sde-v2");

		return {
			useSdeV2,
			// AI pre-extraction readiness gate copy (see @/constants).
			preparingMessage: PAPER_PREPARING_MESSAGE,
			preparingMarkerTooltip: PAPER_PREPARING_MARKER_TOOLTIP,
		};
	},

	data() {
		return {
			deleteDialog: false,
			dialog: false,
			displayDeleted: false,
			downloads: [],
			downloadMode: false,
			fileDialog: false,
			loading: false,
			isAdmin: false,
			options: {
				itemsPerPage: this.sdeQueueMode ? 50 : 10,
				page: 1,
			},
			papers: [],
			restoreDialog: false,
			selectedPaper: {},
			showDownload: false,
			showSelect: false,
			totalPapers: 0,
			searchKey: "",
			// Click-time availability check state. When a queue user clicks
			// "Start Extraction" on a paper that has since been claimed by
			// other extractors, this dialog informs them and the queue is
			// refreshed.
			unavailableDialogOpen: false,
			unavailablePaperName: "",
			// The availability check's `reason`: "unavailable" (claimed by
			// others) or PAPER_PREPARING (AI pre-extraction still running).
			unavailableReason: "",
			// Multi-extraction selection state. When the user holds MORE THAN
			// ONE open extraction on a paper (e.g. a reopened extraction was
			// reassigned to them while they also have their own in-progress
			// entry), opening it must be an explicit choice, never a silent
			// "newest wins" pick. entrySelectReadOnly remembers which action
			// triggered the dialog (View Paper vs Start Extraction).
			entrySelectDialogOpen: false,
			entrySelectEntries: [],
			entrySelectPaper: null,
			entrySelectReadOnly: false,
			queueMode:
				this.adminQueueMode ||
				this.sdeQueueMode ||
				this.trainingQueueMode ||
				this.qcMode,
			allHeaders: [
				{
					title: "Priority",
					value: "queue_admin_actions",
					align: "center",
					sortable: false,
					width: "50px",
					modes: ["queue-admin"],
				},
				{
					title: "Paper name",
					value: "name",
					align: "left",
					modes: ["admin", "queue", "queue-admin", "training"],
				},
				{
					title: "Priority",
					value: "priority",
					align: "center",
					width: "50px",
					modes: ["admin", "queue-admin"],
				},
				{
					title: "DOI",
					value: "doi",
					width: "200px",
					modes: ["admin"],
				},
				{
					title: "Projects",
					value: "projects",
					align: "center",
					width: "150px",
					modes: ["admin", "queue", "queue-admin"],
				},
				{
					title: "File count",
					value: "file_count",
					align: "center",
					width: "100px",
					modes: ["admin", "queue", "queue-admin", "training"],
				},
				{
					title: "Created at",
					value: "created_at",
					align: "center",
					divider: true,
					width: "200px",
					modes: ["admin", "queue", "queue-admin", "training"],
				},
				{
					title: "In-progress",
					value: "in_progress",
					align: "center",
					divider: true,
					width: "200px",
					modes: ["queue", "queue-admin"],
				},
				{
					title: "Completed",
					value: "completed",
					align: "center",
					divider: true,
					width: "200px",
					modes: ["queue", "queue-admin"],
				},
				{
					title: "Actions",
					value: "admin_actions",
					align: "center",
					width: "95px",
					sortable: false,
					modes: ["admin"],
				},
				{
					title: "Actions",
					value: "queue_actions",
					align: "center",
					width: "80px",
					sortable: false,
					modes: ["queue", "queue-admin", "training"],
				},
			],
		};
	},

	computed: {
		headers() {
			let headers = this.allHeaders.filter((header) =>
				header.modes.includes(this.mode),
			);

			// Orphaned papers have no project by definition, so the Projects
			// column would be an always-empty red-alert column - drop it.
			if (this.orphanedMode) {
				headers = headers.filter((header) => header.value !== "projects");
			}

			return headers;
		},
		mode() {
			let mode = "admin";

			if (this.training) {
				mode = "training";
			} else if (this.queueMode) {
				if (this.isAdmin) {
					mode = "queue-admin";
				} else {
					mode = "queue";
				}
			}

			return mode;
		},
		paper() {
			return this.qcMode ? useMainStore().qcPaper : useMainStore().paper;
		},
		title() {
			if (this.orphanedMode) {
				return "Orphaned Papers";
			}
			if (this.training && this.trainingQueueMode) {
				return "Training Queue";
			}
			return this.queueMode ? "Work Queue" : "Papers";
		},
		// Orphaned mode keeps the reassuring safety-net copy the standalone page
		// had; every other mode uses Vuetify's default empty text.
		noDataText() {
			return this.orphanedMode
				? "No orphaned papers. Every paper is attached to at least one project."
				: "No data available";
		},
		// "Add new paper" is hidden in orphaned mode (nothing to add to the orphan
		// list) and when embedded in a project view (which provides its own
		// project-locked Add Paper button); shown in the normal admin table.
		showAddPaper() {
			return !this.queueMode && !this.orphanedMode && !this.projectUuid;
		},
		// The search term actually applied to the fetch. When embedded, the parent
		// owns the search box and feeds it in via `externalSearch`; otherwise the
		// internal search field's `searchKey` is used.
		activeSearchKey() {
			return this.embedded ? this.externalSearch : this.searchKey;
		},
		user() {
			return useMainStore().user;
		},
		entrySelectPaperName() {
			return this.entrySelectPaper ? this.entrySelectPaper.name || "" : "";
		},
		// The paper is not claimed, just not ready: its AI pre-extraction is
		// still running, so the dialog must not say "claimed by another
		// extractor".
		unavailableIsPreparing() {
			return this.unavailableReason === PAPER_PREPARING;
		},
	},

	watch: {
		options: {
			deep: true,
			handler() {
				this.sdeQueueMode ? this.getExtractionQueue() : this.getPapers();
			},
		},
		downloadMode(val) {
			if (val) {
				this.showSelect = true;
			} else {
				this.showSelect = false;
				this.downloads = [];
			}
		},
		displayDeleted() {
			this.options.page = 1;
			this.getPapers();
		},
		searchKey(newVal, oldVal) {
			if (newVal?.length >= 2 || oldVal?.length >= 2) {
				this.options.page = 1;
				this.sdeQueueMode ? this.getExtractionQueue() : this.getPapers();
			}
		},
		// Embedded mode: the parent's search feeds in through `externalSearch`, so
		// mirror the internal search watcher's threshold/reset behavior here.
		externalSearch(newVal, oldVal) {
			if (!this.embedded) return;
			if (newVal?.length >= 2 || oldVal?.length >= 2) {
				this.options.page = 1;
				this.getPapers();
			}
		},
	},

	mounted() {
		this.checkUserDownloadPermission();
	},

	methods: {
		...mapActions(useMainStore, [
			"setPaper",
			"setQCPaper",
			"setSaveUUID",
			"showNotification",
		]),
		getRowProps(paper) {
			return {
				class: this.isDeleted(paper),
			};
		},
		isDeleted(paper) {
			return paper.deleted_at ? "deleted" : "";
		},
		getFileCount(paper) {
			// Filter out save data (WPD-era JSON blobs riding on the paper).
			// On a ClinicalTrials.gov paper (nct_id set) the registry JSON IS
			// the extraction document, so it counts as a real file there -
			// otherwise CTDG papers show 0 files and Start stays disabled.
			const count = _.filter(paper.files, (doc) => {
				return doc.mime_type !== "application/json" || !!paper.nct_id;
			}).length;

			return count;
		},
		getCreatedTimestamp(paper) {
			return new Date(paper.created_at).toLocaleString();
		},
		checkUserDownloadPermission() {
			this.showDownload = useMainStore().can("download_data");
		},
		checkUserEditPermission(rawPapers) {
			this.isAdmin = useMainStore().canAll(["delete_file", "upload_file"]);
			this.papers = rawPapers;
		},
		clearSelectedPaper() {
			setTimeout(() => {
				this.selectedPaper = {};

				if (this.$refs.paperEdit) {
					this.$refs.paperEdit.clear();
				}
			}, 300);
		},
		remove(item) {
			this.selectedPaper = Object.assign({}, item);

			this.deleteDialog = true;
		},
		closeDeleteDialog() {
			this.deleteDialog = false;

			this.clearSelectedPaper();
		},
		restore(item) {
			this.selectedPaper = Object.assign({}, item);

			this.restoreDialog = true;
		},
		closeRestoreDialog() {
			this.restoreDialog = false;

			this.clearSelectedPaper();
		},
		deletePaper() {
			this.loading = true;

			if (this.selectedPaper) {
				this.$http
					.delete(`/papers/${this.selectedPaper.uuid}`)
					.then((response) => {
						// display success
						this.showNotification(response.data);

						// refresh table
						this.getPapers();
					})
					.catch((error) => {
						// display error
						if (error.response) {
							if (error.response.data.message) {
								this.showNotification(error.response.data);
							} else {
								this.showNotification(error.response.data.error);
							}
						}

						this.loading = false;
					});
			} else {
				this.loading = false;
			}

			this.closeDeleteDialog();
		},
		restorePaper() {
			this.loading = true;

			if (this.selectedPaper) {
				this.$http
					.put(`/papers/${this.selectedPaper.uuid}`, {
						deletedAt: null,
					})
					.then((response) => {
						this.showNotification(response.data);
						this.getPapers();
					})
					.catch((error) => {
						if (error.response) {
							if (error.response.data.message) {
								this.showNotification(error.response.data);
							} else {
								this.showNotification(error.response.data.error);
							}
						}

						this.loading = false;
					});
			} else {
				this.loading = false;
			}

			this.closeRestoreDialog();
		},
		edit(item) {
			// When embedded (e.g. ProjectView), this table's own edit dialog is
			// suppressed (showAddPaper is false with a projectUuid), so hand the row up
			// to the parent to open PaperEdit rather than toggling a dialog that never
			// renders. Non-embedded usages (Paper Management) are unchanged. (DCTL-2093)
			if (this.embedded) {
				this.$emit("edit-paper", item);
				return;
			}

			this.selectedPaper = Object.assign({}, item);

			this.dialog = true;
		},
		closeDialog() {
			this.dialog = false;

			setTimeout(() => {
				this.clearSelectedPaper();
			}, 250);
		},
		closeDialogAndRefresh() {
			this.closeDialog();

			this.$nextTick(() => {
				this.getPapers();
			});
		},
		getPapers() {
			this.loading = true;

			let filter = undefined;
			if (this.qcMode) {
				filter = "readyForQC";
			}

			const { itemsPerPage, page } = this.options;
			const sortBy = this.queueMode ? null : ["created_at"];
			const sortDesc = this.queueMode ? null : [true];
			this.$http
				.get("/papers", {
					params: {
						...(this.displayDeleted ? { deletedOnly: true } : {}),
						...(!this.displayDeleted ? { omitDeleted: true } : {}),
						...(this.training ? { trainingOnly: true } : {}),
						...(this.projectUuid ? { projectUuid: this.projectUuid } : {}),
						...(this.orphanedMode ? { orphaned: true } : {}),
						filter: filter,
						itemsPerPage: itemsPerPage,
						page: page,
						searchKey:
							this.activeSearchKey && this.activeSearchKey.length >= 2
								? this.activeSearchKey
								: "",
						sortBy: sortBy,
						sortDesc: sortDesc,
					},
				})
				.then(async (response) => {
					// only check access level in queue mode
					if (this.queueMode) {
						await this.checkUserEditPermission(response.data.papers.data);
					} else {
						this.papers = response.data.papers.data;
					}

					this.totalPapers = parseInt(response.data.papers.meta.total, 10);
				})
				.catch((error) => {
					// display error
					if (error.response) {
						if (error.response.data.message) {
							this.showNotification(error.response.data);
						} else {
							this.showNotification(error.response.data.error);
						}
					}
				})
				.finally(() => {
					this.loading = false;
				});
		},
		getExtractionQueue() {
			this.loading = true;
			const { itemsPerPage, page } = this.options;
			this.$http
				.get("/papers/extraction-queue", {
					params: {
						searchKey:
							this.activeSearchKey && this.activeSearchKey.length >= 2
								? this.activeSearchKey
								: "",
						userUUID: this.user.uuid,
						itemsPerPage: itemsPerPage,
						page: page,
					},
				})
				.then(async (response) => {
					this.papers = response.data.papers.data;
					this.totalPapers = parseInt(response.data.papers.meta.total, 10);
				})
				.catch((error) => {
					// display error
					if (error.response) {
						if (error.response.data.message) {
							this.showNotification(error.response.data);
						} else {
							this.showNotification(
								error.response.data?.error ?? "An error occurred",
							);
						}
					}
				})
				.finally(() => {
					this.loading = false;
				});
		},
		async selectPaper(paper) {
			if (this.qcMode) {
				this.setQCPaper(paper);
				this.startQualityControl(paper);
				return;
			}
			// Re-check availability at click time so the user isn't silently
			// added as a 3rd extractor when another claim landed between the
			// queue fetch and this click. Skipped in admin/non-queue views
			// where the eligibility filter doesn't apply.
			// Click-time availability check fires for any queue-mode view
			// that starts an extraction. sdeQueueMode is the standard
			// extractor's queue; adminQueueMode is the admin/QC/group-lead
			// dashboard view, whose queue is also subject to the
			// 2-extractor cap at save time, so the same race exists.
			if (this.sdeQueueMode || this.adminQueueMode) {
				const available = await this.checkPaperAvailability(paper);
				if (!available) return;
			}
			this.setPaper(paper);
			this.startDataEntry(paper);
		},
		async checkPaperAvailability(paper) {
			try {
				const { data } = await this.$http.get(
					`papers/${paper.uuid}/availability`,
				);
				if (data.available) return true;
				this.unavailablePaperName = paper.name || "this paper";
				this.unavailableReason = data.reason || "";
				this.unavailableDialogOpen = true;
				// Refresh whichever list the current view is using —
				// matches the watcher behavior at the top of this file.
				this.sdeQueueMode ? this.getExtractionQueue() : this.getPapers();
				return false;
			} catch (err) {
				// Fail open on network error: proceed with the existing flow
				// (matches today's behavior; no regression from the check).
				console.error("Availability check failed", err);
				return true;
			}
		},
		// All of the current user's open extractions on this paper: never
		// completed, or completed but reopened for rework. More than one can
		// coexist (e.g. a completed extraction reopened and reassigned to a
		// user who also holds their own in-progress entry), so the open flows
		// below branch on the full list instead of silently taking the newest.
		userOpenDataEntries(paper) {
			return (paper.dataEntries || []).filter(
				(de) =>
					de.user.uuid === this.user.uuid &&
					(!de.completed_at || de.reopened_at),
			);
		},
		openEntrySelectDialog(paper, entries, readOnly) {
			this.entrySelectPaper = paper;
			this.entrySelectEntries = entries;
			this.entrySelectReadOnly = readOnly;
			this.entrySelectDialogOpen = true;
		},
		onEntrySelected(entry) {
			this.entrySelectDialogOpen = false;

			const paper = this.entrySelectPaper;
			if (!paper || !entry) return;

			if (this.entrySelectReadOnly) {
				this.openReadOnlyEntry(paper, entry);
			} else {
				this.openDataEntry(paper, entry);
			}
		},
		// DCTL-2638. The local open-entry list comes from the queue fetch, so
		// a tab loaded before this user's first save on a paper shows none
		// even though a row now exists. Minting a fresh uuid on that stale
		// view is what created a second, blank extraction next to the real
		// one (prod, 2026-09-10). So when the list is empty, ask the server
		// at click time (as ProjectTab.startOrContinue does) and mint only
		// when it also says there is none.
		//
		// Resolves to { ok: true, entries } - EVERY open extraction the server
		// holds for this user on this paper, newest first, each already in
		// paper.dataEntries shape - or to { ok: false } when the lookup failed
		// (already reported; the caller must stop without navigating, because
		// minting blind is the bug). The list can hold more than one: a user
		// can own two open extractions on a paper via reopen + reassign, and
		// picking the newest for them silently opens the wrong one, which is
		// what the select dialog exists to prevent.
		//
		// Training mode skips the lookup: start-info only resolves
		// non-training entries (is_training = false), so the one thing it
		// could return there is the user's REAL extraction, which must never
		// open in the training editor. The server-side born-new guard
		// (duplicate_open_entry) still covers a training duplicate.
		//
		// `readOnly` marks the View Paper path: the read-only viewer never
		// saves, so start-info skips the AI pre-extraction readiness gate,
		// which otherwise refuses to START a new extraction on a paper whose
		// pre-extraction is still running.
		async resolveExistingEntry(paper, { readOnly = false } = {}) {
			if (this.training) return { ok: true, entries: [] };
			try {
				// openOnly: resolve only extractions that can still be
				// continued, and get all of them. Without it start-info also
				// returns a COMPLETED entry (the kanban wants that, its cards
				// represent finished work), and this queue would then reopen
				// finished work instead of starting the re-extraction the
				// click asked for.
				const { data } = await this.$http.get(
					`/papers/${paper.uuid}/start-info`,
					{ params: { openOnly: true, ...(readOnly ? { readOnly } : {}) } },
				);
				return { ok: true, entries: data.openDataEntries || [] };
			} catch (error) {
				this.handleError(error);
				return { ok: false };
			}
		},
		// Mirrors ProjectTab.handleError.
		handleError(error) {
			if (error.response?.data?.code === PAPER_PREPARING) {
				this.showNotification({
					message: PAPER_PREPARING_MESSAGE,
					status: "info",
				});
			} else if (error.response?.data?.message) {
				this.showNotification(error.response.data);
			} else if (error.response?.data?.error) {
				this.showNotification(error.response.data.error);
			} else {
				this.showNotification({
					message: "Something went wrong. Please try again.",
					status: "error",
				});
			}
		},
		async startReadOnlyDataEntry(paper) {
			this.setPaper(paper);

			const openEntries = this.userOpenDataEntries(paper);
			if (openEntries.length > 1) {
				this.openEntrySelectDialog(paper, openEntries, true);
				return;
			}

			let entry = openEntries[0] ?? null;
			if (!entry) {
				const resolved = await this.resolveExistingEntry(paper, {
					readOnly: true,
				});
				if (!resolved.ok) return;
				// DCTL-2638: the server can hold several open entries the stale
				// list never saw. Choosing for the user is the wrong-extraction
				// bug, so hand the same dialog the local >1 branch uses.
				if (resolved.entries.length > 1) {
					this.openEntrySelectDialog(paper, resolved.entries, true);
					return;
				}
				entry = resolved.entries[0] ?? null;
			}
			this.openReadOnlyEntry(paper, entry);
		},
		openReadOnlyEntry(paper, entry) {
			const sdeUuid = entry ? entry.uuid : uuidv4();
			this.setSaveUUID(sdeUuid);
			// Open the read-only viewer in SDEv2 when either:
			//   1. the entry was saved in V2 format (DACTYL_SDE), or
			//   2. the SDEv2 toggle is on (or this is a ClinicalTrials.gov
			//      paper, which is SDEv2-only) and there is no entry yet.
			// Otherwise fall back to the SDEv1 (WPD) viewer.
			const useV2 =
				entry?.data_format === "DACTYL_SDE" ||
				((this.useSdeV2 || !!paper.nct_id) && !entry);
			const base = useV2
				? "/source-data-extraction-read-only"
				: "/data-entry-read-only";
			this.$router.push(`${base}/${paper.uuid}/${sdeUuid}`);
		},
		async startDataEntry(paper) {
			const openEntries = this.userOpenDataEntries(paper);
			if (openEntries.length > 1) {
				this.openEntrySelectDialog(paper, openEntries, false);
				return;
			}

			let entry = openEntries[0] ?? null;
			if (!entry) {
				const resolved = await this.resolveExistingEntry(paper);
				if (!resolved.ok) return;
				// DCTL-2638: the server can hold several open entries the stale
				// list never saw. Choosing for the user is the wrong-extraction
				// bug, so hand the same dialog the local >1 branch uses.
				if (resolved.entries.length > 1) {
					this.openEntrySelectDialog(paper, resolved.entries, false);
					return;
				}
				entry = resolved.entries[0] ?? null;
			}
			this.openDataEntry(paper, entry);
		},
		openDataEntry(paper, entry) {
			const sdeUuid = entry ? entry.uuid : uuidv4();

			this.setSaveUUID(sdeUuid);

			// Format is fixed at creation. An already-started extraction always
			// reopens in the editor matching its stored data_format, regardless of
			// the SDEv2 flag - otherwise an SDEv1 extraction would load in the
			// SDEv2 editor (reading an empty blob) and orphan its data on save. The
			// flag only chooses the editor for a brand-new / not-yet-saved entry
			// (no stored format yet). ClinicalTrials.gov papers (nct_id set) are
			// SDEv2-only: the registry JSON seed/renderer has no SDEv1 analog.
			// The format decision comes from the SELECTED entry, never a
			// paper-level guess: with several open entries they can differ.
			const startedFormat = entry?.data_format;
			const useV2 = startedFormat
				? startedFormat === "DACTYL_SDE"
				: this.useSdeV2 || !!paper.nct_id;

			if (useV2) {
				this.$router.push(
					this.training
						? `/source-data-extraction-training/${paper.uuid}/${sdeUuid}`
						: `/source-data-extraction/${paper.uuid}/${sdeUuid}`,
				);
			} else {
				this.$router.push(
					this.training
						? `/data-entry-training/${paper.uuid}/${sdeUuid}`
						: `/data-entry/${paper.uuid}/${sdeUuid}`,
				);
			}
		},
		startQualityControl() {
			// TODO get in-progress quality control work
			this.$router.push("/quality-control");
		},

		setPriority(paper) {
			this.$http
				.put(`papers/${paper.uuid}`, { priority: paper.priority })
				.then(() => {
					// refresh table
					this.getPapers();
				})
				.catch((error) => {
					// display error
					if (error.response) {
						if (error.response.data.message) {
							this.showNotification(error.response.data);
						} else {
							this.showNotification(error.response.data.error);
						}
					}
				});
		},
		downloadFile(file) {
			this.loading = true;

			this.$http
				.get(`/files/download/${file.uuid}`, {
					responseType: "blob",
				})
				.then((response) => {
					const fileName =
						filenameFromContentDisposition(
							response.headers["content-disposition"],
						) || file.name;
					fileDownload(response.data, fileName);
				})
				.catch((error) => {
					if (error.message) {
						this.showNotification({
							message: "File not found",
							status: "error",
						});
					} else if (error.response) {
						if (error.response.data.message) {
							this.showNotification(error.response.data);
						} else {
							this.showNotification(error.response.data.error);
						}
					}
				})
				.finally(() => {
					this.loading = false;
				});
		},
		downloadPaperData() {
			this.$http
				.get("/papers/download", {
					params: {
						paperUUIDs: this.downloads.map((paper) => paper.uuid),
					},
					responseType: "blob",
				})
				.then((response) => {
					const fileName =
						filenameFromContentDisposition(
							response.headers["content-disposition"],
						) || "dactyl-data.json";
					fileDownload(response.data, fileName);
				})
				.catch((error) => {
					// display error
					if (error.message) {
						this.showNotification({
							message: "File not found",
							status: "error",
						});
					} else if (error.response) {
						if (error.response.data.message) {
							this.showNotification(error.response.data);
						} else {
							this.showNotification(error.response.data.error);
						}
					}
				})
				.finally(() => {
					this.downloadMode = false;
					this.loading = false;
				});
		},
		inProgressDataEntries(paper) {
			return _.uniqBy(
				paper.dataEntries.filter(
					(de) =>
						(!de.completed_at || de.reopened_at) &&
						this.isSdeUser(de.user) &&
						!de.user.deleted_at,
				),
				"user_id",
			);
		},
		completedDataEntries(paper) {
			return _.uniqBy(
				paper.dataEntries.filter(
					(de) =>
						!!de.completed_at && !de.reopened_at && this.isSdeUser(de.user),
				),
				"user_id",
			);
		},
		isSdeUser(user) {
			return (
				!_.isEmpty(user.roles) &&
				user.roles.some((role) => extractionRoleSlugs.includes(role.slug))
			);
		},
	},
};
</script>

<style scoped lang="scss">
:deep(.paper-table) {
	.v-icon--disabled {
		pointer-events: all !important;
	}

	&.v-data-table .v-table__wrapper td {
		font-size: 12px;
	}

	&.v-data-table .v-table__wrapper th {
		font-size: 12px;
		font-weight: 600;
		color: rgba(0, 0, 0, 0.6);
		height: 48px;
	}
}

:deep(.paper-table-priority-text) {
	width: 50px;
	height: 100% !important;
	font-size: 14px !important;
}

.paper-toolbar-search {
	min-width: 220px;
	max-width: 320px;
}

// Embedded in a parent tab (Project View's Papers tab): square off the top
// corners so the primary toolbar sits flush under the parent's tab strip. The
// card clips its toolbar to these corners, so flattening them here is enough.
.paper-table--embedded {
	border-top-left-radius: 0;
	border-top-right-radius: 0;
}
</style>
